/* eslint-disable @typescript-eslint/no-explicit-any */
import { globals } from "@/main";
import { regenerateTokenService } from "@/services/apis/auth.service";
import { listMediaService } from "@/services/apis/media.service";
import { queryParams } from "@/services/utils/helper.util";
import {
  defineComponent,
  ref,
  computed,
  PropType,
  onMounted,
  watch,
} from "vue";

import MediaFromComputer from "./components/from-computer/index.vue";

type Directory = {
  code: string;
  title: string;
};
const initialDirectoryValue: Directory = { code: "", title: "" };

type FormType = {
  code: string[]; // Define the type of 'code' array
};

type MetaValue = {
  offset?: number;
  limit?: number;
  total?: number;
  page?: number;
  totalPage?: number;
};

export default defineComponent({
  name: "c-icon",

  props: {
    value: {
      type: [String, Number, Array] as PropType<
        string | number | (string | number)[]
      >,
      validator: () => true,
    },
    loading: { type: Boolean, default: false },
    style: { type: String, default: "" },
    class: { type: String, default: "" },
    format: { type: String, default: "JPG, PNG, SVG" },
    icon: { type: String, default: "" },
    schema: { type: Function, default: () => ({}) },
    refIds: { type: String, default: "refcomponentUpload" },
    isDelete: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    isArray: { type: Boolean, default: false },
    code: { type: String, default: "key" },
  },

  components: {
    MediaFromComputer,
  },

  emits: ["update:value", "enter"],

  setup(props, { emit }) {
    const className = ref(props.class);
    const styleName = ref(props.style);
    const uploadIcon = ref(
      props.icon || require("@/assets/icons/sidebars/add-image.svg")
    );
    const newValue = computed(() => props.value);
    const isModal = ref(false);
    const meta = ref<MetaValue>({
      offset: 0,
      limit: 50,
      total: 0,
      page: 1,
      totalPage: 1,
    });

    const onUpdateValue = (): any => {
      emit(
        "update:value",
        form.value.code && form.value.code.length
          ? form.value.code
          : props.value
      );
      checkScroll();
      isModal.value = false;
      resetFrom();
    };

    const onBrowseFile = (): any => {
      isModal.value = true;
    };

    const onCloseBrowseFile = (): any => {
      isModal.value = false;
      resetFrom();
    };

    const isLoading = computed(() => {
      return props.loading;
    });

    // start check scroll class
    const hasScroll = ref(false);

    const checkScroll = () => {
      const imageThumbnail = document.querySelector(
        ".icon-thumbnail"
      ) as HTMLElement;

      const valueLength = Array.isArray(newValue.value)
        ? newValue.value.length
        : 1;
      const imageWidth = 147 * valueLength;

      if (imageWidth > imageThumbnail.clientWidth) {
        hasScroll.value = true;
      } else {
        hasScroll.value = false;
      }
    };
    // end check scroll class

    const onConfirmDelete = (data: any): void => {
      emit("update:value", data);
      checkScroll();
    };

    const folders = globals.$store?.state?.medias || [];

    const tabFolders = [
      {
        code: "S001",
        value: "componentFromServer",
        title: "componentFromServer",
      },
      {
        code: "C001",
        value: "componentFromComputer",
        title: "componentFromComputer",
      },
    ];

    const activeFolder = ref("S001");

    const onTabChange = (value: any): void => {
      activeFolder.value = value;
      isDirectory.value = {
        code: "",
        title: "",
      };
      form.value.code = [];
      meta.value = {
        offset: 0,
        limit: 50,
        total: 0,
        page: 1,
        totalPage: 1,
      };
    };

    const isDirectory = ref(initialDirectoryValue);
    const isDirectoryLoading = ref(true);
    const directoriesImage = ref([]);

    const getMedia = async (): Promise<void> => {
      const folder = isDirectory.value;

      const params = queryParams({
        folder: folder?.code,
        type: 1, // Enum(1:IMAGE, 2:VIDEO, 3:DOCUMENT)
        offset: meta.value?.offset,
        limit: meta.value?.limit,
      });

      await listMediaService(params).then(async (response) => {
        if (response?.expired) {
          const refresh = await regenerateTokenService();
          if (refresh) {
            return await getMedia();
          }
        }

        directoriesImage.value =
          (response?.data &&
            response?.data.length && [
              ...directoriesImage.value,
              ...response?.data,
            ]) ||
          [];
        meta.value = response?.meta;
        isDirectoryLoading.value = false;
      });
    };

    const goBackDirectory = (): void => {
      resetFrom();
    };

    const resetFrom = (): void => {
      isDirectoryLoading.value = false;
      isDirectory.value = {
        code: "",
        title: "",
      };
      directoriesImage.value = [];
      form.value.code = [];
      meta.value = {
        offset: 0,
        limit: 50,
        total: 0,
        page: 1,
        totalPage: 1,
      };
    };

    const goDirectory = (value: any): void => {
      isDirectory.value = value;
      isDirectoryLoading.value = true;

      if (isDirectory.value) {
        getMedia();
      }
    };

    const form = ref<FormType>({
      code: [],
    });

    const onChangeSelected = (): void => {
      checkScroll();
    };

    const onSelectMediaOnly = (media: any): void => {
      form.value.code = media;
    };

    const listMediaBody = ref<HTMLElement | null>(null);

    const handleScroll = (event: Event): void => {
      const target = event.target as HTMLElement;

      if (target.scrollHeight - target.scrollTop === target.clientHeight) {
        if (
          meta.value?.page !== undefined &&
          meta.value?.totalPage !== undefined &&
          meta.value.page < meta.value.totalPage &&
          !isDirectoryLoading.value
        ) {
          getMedia();
        }
      }
    };

    onMounted(() => {
      checkScroll();
    });

    watch([() => props.value], () => {
      checkScroll();
    });

    return {
      className,
      styleName,
      isLoading,
      uploadIcon,
      onUpdateValue,
      newValue,
      onBrowseFile,
      hasScroll,
      isModal,
      onCloseBrowseFile,
      onConfirmDelete,
      folders,
      tabFolders,
      activeFolder,
      onTabChange,
      isDirectory,
      goDirectory,
      isDirectoryLoading,
      directoriesImage,
      goBackDirectory,
      isArr: props.isArray,
      onChangeSelected,
      form,
      onSelectMediaOnly,
      handleScroll,
      listMediaBody,
    };
  },
});
